import React, { forwardRef, useContext } from 'react';
import styles from '../Input.module.css';
import { InputContext } from '../Input';
import {
  passthroughChildrenWithDisabledState,
  isButtonElement,
  isIconButtonElement,
} from '../__utils';

/**
 * Generic slot container for custom start-side content (buttons, icons, dropdowns, etc.)
 * More flexible than InputPrefix which only accepts text strings.
 *
 * Capabilities:
 * - Render any React components (Button, Icon, IconButton, custom content)
 * - Automatically propagate disabled state to children
 * - Attach increment/decrement props to button children for spinners/incrementers
 * - Responsive layout adjustments when containing interactive buttons
 *
 * Use InputStartSlot when:
 * - Displaying interactive elements (buttons, custom components)
 * - Complex content that text-only prefix can't handle
 * Use InputPrefix when:
 * - Simple text labels ("$", "+1", etc.)
 * - No interactive elements needed
 */
interface SlotProps {
  /** Any React content to render in the slot */
  children: React.ReactNode;
  /** Additional CSS classes to apply */
  className?: string;
  /** Props to pass to increment button (for spinners/step controls) */
  increment?: boolean | object;
  /** Props to pass to decrement button (for spinners/step controls) */
  decrement?: boolean | object;
}

/**
 * Start slot component for generic custom content.
 * Automatically propagates disabled/invalid state from InputContext to children.
 * Handles special button props (increment/decrement) for spinner controls.
 */
const InputStartSlot = forwardRef<HTMLDivElement, SlotProps>(
  ({ children, className, increment, decrement }, ref) => {
    // Get disabled/invalid state from parent Input
    const state = useContext(InputContext);

    // Propagate disabled state to all children (buttons, icons, etc.)
    // This ensures consistent disabled styling across all slot content
    let processedChildren = passthroughChildrenWithDisabledState(
      children,
      state.isDisabled,
    );

    // If increment/decrement props provided, attach them to button children
    // Used for spinner/incrementer controls where buttons need special props
    if (increment || decrement) {
      processedChildren = React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          (isButtonElement(child) || isIconButtonElement(child))
        ) {
          // Clone the button and merge in increment/decrement props
          return React.cloneElement(child, {
            type: 'button',
            ...(typeof increment === 'object' && increment !== null
              ? increment
              : {}),
            ...(typeof decrement === 'object' && decrement !== null
              ? decrement
              : {}),
          });
        }
        return child; // Non-button children pass through unchanged
      });
    }

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__startSlot']} 
          ${className || ''}
        `}
        // Propagate disabled/invalid state from InputContext to DOM for CSS selectors
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        // Set data attribute if slot contains buttons (triggers CSS padding adjustments)
        data-start-slot-button={
          React.Children.toArray(children).some(isButtonElement)
            ? 'true'
            : undefined
        }
      >
        {processedChildren}
      </div>
    );
  },
);
InputStartSlot.displayName = 'Input.StartSlot';

export default InputStartSlot;
